/**
 * Redirect paths for the two sign-in flows. Kept free of native imports so the
 * contract can be tested without booting the Expo preset.
 *
 * These are NOT interchangeable:
 *
 *   social flow  → `<origin>/login`         (how the Microsoft provider is registered)
 *   hosted OIDC  → `<origin>/auth/callback` (the blocks-oidc client)
 *
 * `social/initiate` echoes whatever `redirectUri` it is handed into the authorize
 * URL without validating it, so using the OIDC path on the social flow does not
 * fail at sign-in — it yields a code that cannot be redeemed and dies at the
 * callback instead. That was the "first press fails, second works" bug: while
 * signed out `/auth/login-options` is 401, so the fallback provider carried no
 * registered URI and the picker fell through to the OIDC path.
 */

export const SSO_CALLBACK_PATH = "/auth/callback";
export const SOCIAL_CALLBACK_PATH = "/login";

/** Join an origin and a path without doubling or dropping the separator. */
export const buildRedirectUri = (origin: string, path: string): string =>
  `${origin.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;

/**
 * The provider's registered redirect URI for this origin, if it has one.
 *
 * Prefers this origin's own social callback (`<origin>/login`) over any other
 * entry on the same host. The provider also lists the Android app's App Link
 * callback on the same host, and Android hands that URL to the app — so taking
 * whichever same-origin entry happens to come first would let list order send
 * web sign-ins into the app.
 */
export const pickSameOriginRedirectUri = (
  registered: readonly string[],
  origin: string,
): string | undefined => {
  const own = buildRedirectUri(origin, SOCIAL_CALLBACK_PATH);
  if (registered.includes(own)) return own;
  return registered.find((uri) => uri.startsWith(origin));
};
